import { useAtsCandidateDiscoveryApi } from '@/composables/useApi';
import type { PlacePrediction } from './types/place-prediction.type';
import { useSearchStore } from './search.store';
import { useMeStore } from '@/core/shared/me/me.store';
import type { PaginatedResponse } from './types/paginated-response.type';
import type { RecentSearch } from './types/recent-search.type';
import type { SearchGatedUserProfile } from './types/search-gated-user-profile.type';
import { SelectedFilterType } from './types/selected-filter-type.type';
import { DEFAULT_AROUND_RADIUS } from './consts/default-seach-query.const';
import { buildAddressPredictionsUrl } from '@/core/shared/search/utils/build-address-predictions-url.util';
import { isAbortError } from '@/core/shared/errors/abort-error.util';

interface BackendSelectedFilter {
  id?: number;
  value: string | number | string[];
  type: SelectedFilterType;
  includeUnknown?: boolean;
}

// vueuse's useFetch reports an HTTP failure as the Response's statusText, which HTTP/2
// leaves empty — so `new Error(error.value)` reached Sentry as an undiagnosable
// "Error: Error" (FACTORYFIX-UI3-21E). Compose the message from what is diagnostic
// instead: the HTTP status and the backend's error body, which the fetch wrapper keeps
// in `data` even on failure (updateDataOnError in useApi). With no body (e.g. the request
// never got a response), fall back to the raw error so "Failed to fetch" still comes through.
function toRequestError(
  context: string,
  statusCode: number | null,
  body: unknown,
  error: unknown,
): Error {
  const detail = body != null ? JSON.stringify(body) : error ? String(error) : '';
  return new Error(
    `${context} failed (status ${statusCode ?? 'unknown'})${detail ? `: ${detail}` : ''}`,
  );
}

export class SearchPersistence {
  private store = useSearchStore();
  private meStore = useMeStore();

  // Latest-query-wins: a new lookup cancels this consumer's prior in-flight request, so the
  // autocomplete only ever resolves the most recent query. Abandoned in-flight requests (the
  // field cleared, the component unmounted) are left to complete by design — for cheap,
  // idempotent GETs like address predictions, cancel-on-leave isn't worth the wiring.
  private activeAddressPredictionAbort: (() => void) | null = null;

  public async getAddressPredictions(address: string): Promise<PlacePrediction[]> {
    this.activeAddressPredictionAbort?.();

    const url = buildAddressPredictionsUrl(address);
    const { execute, abort, data, error, statusCode } = useAtsCandidateDiscoveryApi(url, {
      immediate: false,
    })
      .get()
      .json<PlacePrediction[]>();

    this.activeAddressPredictionAbort = abort;
    try {
      await execute();
    } finally {
      if (this.activeAddressPredictionAbort === abort) {
        this.activeAddressPredictionAbort = null;
      }
    }

    if (error.value) {
      // An aborted request was superseded by a newer lookup — control flow, not a failure.
      if (isAbortError(error.value)) {
        return [];
      }

      throw toRequestError(
        'Address predictions request',
        statusCode.value,
        data.value,
        error.value,
      );
    }

    return data.value ?? [];
  }

  public async searchCandidates(): Promise<PaginatedResponse<SearchGatedUserProfile>> {
    const filtersRecord = this.store.selectedFiltersRecord;
    const hitsPerPage = this.store.pagination.itemsPerPage || undefined;
    const locationText = filtersRecord[SelectedFilterType.LOCATION]?.value || undefined;
    const { jobLocationCoordinates } = this.store;
    // A radius only means something anchored to a location, so only default one in when a
    // location is being sent. An unconditional default radius reads as user-set server-side
    // and silently overrides an NL-extracted radius (ONE-6178).
    const aroundRadius =
      filtersRecord[SelectedFilterType.AROUND_RADIUS]?.value ||
      (locationText || jobLocationCoordinates ? DEFAULT_AROUND_RADIUS : undefined);
    const page = this.store.pagination.page;
    const query = filtersRecord[SelectedFilterType.KEYWORD]?.value || '';

    // The types sent inside `filters`. KEYWORD, LOCATION and AROUND_RADIUS are staged in the
    // store like the rest but go out as top-level request fields above, so listing them here
    // would send each one twice.
    const allowedFilterTypes = [
      SelectedFilterType.BRAND,
      SelectedFilterType.JOB_TITLE,
      SelectedFilterType.KNOWLEDGE_DISCIPLINE,
      SelectedFilterType.LANGUAGE,
      SelectedFilterType.LAST_ACTIVE,
      SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES,
      SelectedFilterType.OBJECT_ID,
      SelectedFilterType.OPEN_TO_TRAVEL,
      SelectedFilterType.PAY_RATE_MAX,
      SelectedFilterType.PAY_RATE_MIN,
      SelectedFilterType.RESPONSIVENESS,
      SelectedFilterType.SHIFT,
      SelectedFilterType.TECHNOLOGY,
      SelectedFilterType.WILLING_TO_RELOCATE,
      SelectedFilterType.YEARS_OF_EXPERIENCE,
    ];

    const filters = this.store.selectedFilters.filter((f) => allowedFilterTypes.includes(f.type));

    const mappedFilters: BackendSelectedFilter[] = filters.reduce((acc, filter) => {
      const toAdd = [];

      // The backend requires the value to be in the `id` property.
      if (
        filter.type === SelectedFilterType.LAST_ACTIVE ||
        filter.type === SelectedFilterType.LAST_OPEN_TO_OPPORTUNITIES
      ) {
        toAdd.push({ type: filter.type, id: filter.value, value: filter.value });
      }

      // The backend requires a separate filter for each years of experience.
      if (filter.type === SelectedFilterType.YEARS_OF_EXPERIENCE) {
        const filters = filter.value.map((v) => ({ type: filter.type, id: v, value: v }));
        toAdd.push(...filters);
      }

      // Pay bounds must ship as numeric strings — the backend treats any other shape
      // (including a plain number) as a silent no-op rather than an error.
      if (
        filter.type === SelectedFilterType.PAY_RATE_MIN ||
        filter.type === SelectedFilterType.PAY_RATE_MAX
      ) {
        toAdd.push({ type: filter.type, value: `${filter.value}` });
      }

      // One filter with a list value means "any of these"; sibling filters of the same
      // type would AND into "all of these", and the default branch below would corrupt
      // the array into a comma-joined string the backend drops.
      if (
        filter.type === SelectedFilterType.SHIFT ||
        filter.type === SelectedFilterType.LANGUAGE ||
        filter.type === SelectedFilterType.RESPONSIVENESS
      ) {
        toAdd.push({ type: filter.type, value: [...filter.value] });
      }

      // Presence-modeled booleans: staged only when the chip is on, so the wire value is
      // always 'true'.
      if (
        filter.type === SelectedFilterType.WILLING_TO_RELOCATE ||
        filter.type === SelectedFilterType.OPEN_TO_TRAVEL
      ) {
        toAdd.push({ type: filter.type, value: 'true' });
      }

      if (toAdd.length === 0) {
        const value = typeof filter.value === 'number' ? filter.value : `${filter.value}`;

        toAdd.push({
          ...filter,
          value,
        });
      }

      // A filter keeps the mode it was staged with (a recruiter-edited inferred filter can be
      // lenient); anything without one is strict. The server admits unknowns
      // only on `includeUnknown === true`, so the explicit `false` records that stance.
      const includeUnknown = filter.includeUnknown ?? false;
      return [...acc, ...toAdd.map((mapped) => ({ ...mapped, includeUnknown }))];
    }, [] as BackendSelectedFilter[]);

    const { searchMode, jobId, queryMode, documentSearchJobTitle } = this.store;
    const { source, parentSessionId, suppressInferredTypes } = this.store;

    const search = {
      // Job mode fences on the job's resolved coordinates (deterministic geo filter,
      // ONE-6241); otherwise the backend geocodes locationText. locationText is also the
      // human-readable label for the recent-search list.
      location: jobLocationCoordinates ?? undefined,
      locationText,
      aroundRadius,
      page,
      hitsPerPage,
      query,
      filters: mappedFilters,
      // Send the job-mode params for a job-driven search; typed and admin/S2S requests
      // keep the existing keyword request shape.
      ...(searchMode ? { searchMode, jobId, queryMode, documentSearchJobTitle } : {}),
      // On a recent-search rerun, attribute the new session to its parent (ONE-6177).
      ...(source === 'recent' ? { source, parentSessionId } : {}),
      // Inferred filters the recruiter removed for this query; the backend skips
      // re-inferring them (ONE-6178).
      ...(suppressInferredTypes?.length ? { suppressInferredTypes } : {}),
      // Ask the backend to attach candidateInsights to each hit.
      includeInsights: true,
    };

    const employerId = this.meStore.employer?.id;
    const url = `/search-talent/employer/${employerId}/candidates`;

    const { data, error, statusCode } = await useAtsCandidateDiscoveryApi(url)
      .post(search)
      .json<PaginatedResponse<SearchGatedUserProfile>>();

    if (error.value || !data.value) {
      // On failure `data` holds the backend's error body, not results (updateDataOnError).
      throw toRequestError('Candidate search request', statusCode.value, data.value, error.value);
    }

    return data.value;
  }

  // userProfileId is read from the JWT and employerId from the path server-side, so the FE
  // only supplies employerId — identical scoping to the candidates call above.
  public async getRecentSearches(): Promise<RecentSearch[]> {
    const employerId = this.meStore.employer?.id;
    // No employer in session yet (loading/unauthenticated) — skip the call rather than
    // request `/employer/undefined/recent-searches`.
    if (!employerId) {
      return [];
    }
    const url = `/search-talent/employer/${employerId}/recent-searches`;

    const { data, error, statusCode } = await useAtsCandidateDiscoveryApi(url)
      .get()
      .json<RecentSearch[]>();

    if (error.value) {
      throw toRequestError('Recent searches request', statusCode.value, data.value, error.value);
    }

    return data.value ?? [];
  }
}
